

import type { Itinerary, ItineraryDay } from '@/app/lib/types';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Lightbulb, Plane, Train, Car, Bed, CheckCircle, Search, Hotel } from 'lucide-react';
import { ActivityEditor } from './ActivityEditor';
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface TripPlannerAssistantProps {
  itinerary: Itinerary;
  onAddActivity: (dayNumber: number, newActivity: ItineraryDay['activities'][0]) => void;
}

type PlannerStep = 'arrival' | 'transport' | 'complete';

export function TripPlannerAssistant({ itinerary, onAddActivity }: TripPlannerAssistantProps) {
  // Determine what has already been planned
  const hasArrival = itinerary.generatedItinerary.days[0]?.activities.some(act => ['flight', 'train', 'car'].includes(act.category));
  const hasTransport = itinerary.generatedItinerary.days.some(day => day.activities.some(act => act.category === 'car'));
  
  const [transportHandled, setTransportHandled] = useState(hasTransport);


  let currentStep: PlannerStep = 'complete';
  if (!hasArrival) {
    currentStep = 'arrival';
  } else if (!transportHandled) {
    currentStep = 'transport';
  }
  
  const handleOwnCar = () => {
    onAddActivity(1, {
      time: 'Travel',
      description: 'Travel with own car.',
      locationName: 'Own Transport',
      locationSlug: 'own-transport',
      category: 'car',
      coordinates: { lat: 0, lng: 0 },
    });
    setTransportHandled(true);
  };


  const renderArrivalStep = () => (
    <div>
      <p className="text-sm text-muted-foreground mb-4">How are you arriving for your trip?</p>
      <div className="flex flex-wrap gap-2">
        <ActivityEditor
          onSave={(activity) => onAddActivity(1, activity)}
          triggerButton={
            <Button variant="outline"><Plane className="mr-2 h-4 w-4" /> Add Flight</Button>
          }
        />
        <ActivityEditor
          onSave={(activity) => onAddActivity(1, activity)}
          triggerButton={
            <Button variant="outline"><Train className="mr-2 h-4 w-4" /> Add Train</Button>
          }
        />
        <ActivityEditor
          onSave={(activity) => onAddActivity(1, activity)}
          triggerButton={
            <Button variant="outline"><Car className="mr-2 h-4 w-4" /> Add Drive</Button>
          }
        />
      </div>
    </div>
  );

  const renderTransportStep = () => (
    <div>
      <p className="text-sm text-muted-foreground mb-4">Will you need to rent a car for your trip?</p>
       <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline">
            <Link to="/car-rental/search">
                <Car className="mr-2 h-4 w-4" /> Find Car Rental
            </Link>
        </Button>
        <Button variant="ghost" onClick={handleOwnCar}>I have my own car</Button>
      </div>
    </div>
  );
  
  const renderCompleteStep = () => (
     <div className="flex items-center gap-3">
        <CheckCircle className="h-6 w-6 text-green-500" />
        <p className="text-sm text-muted-foreground">Your core travel plans are set! Remember to book accommodation for each night.</p>
    </div>
  );

  const renderStepContent = () => {
    switch(currentStep) {
        case 'arrival': return renderArrivalStep();
        case 'transport': return renderTransportStep();
        case 'complete': return renderCompleteStep();
        default: return null;
    }
  };

  if (currentStep === 'complete') {
    return null; // Don't show the assistant if everything is planned
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3">
          <Lightbulb className="h-6 w-6 text-yellow-400" />
          Trip Planning Assistant
        </CardTitle>
      </CardHeader>
      <CardContent>
        {renderStepContent()}
      </CardContent>
    </Card>
  );
}
